import type React from 'react';

import type { QuickFactStickerName } from '@rover/react-lib/src/pages/landing/components/quickFactStickers';
import { QUICK_FACT_STICKERS } from '@rover/react-lib/src/pages/landing/components/quickFactStickers';
import type { LocalizedBrandServicePetType } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandServicePetType';
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import type {
  ServiceLandingFormatNumber,
  ServiceLandingQuickFactBands,
  ServiceLandingQuickFactPetKey,
  ServiceLandingQuickFactText,
} from '../defaultServiceLandingQuickFactContent';
import { DEFAULT_SERVICE_LANDING_QUICK_FACTS } from '../defaultServiceLandingQuickFactContent';

// The sticker of each band follows the page's pet type, also when the copy is for any pet.
const STICKERS_BY_BAND = {
  first: { dog: 'chihuahua', cat: 'orangeCat' },
  second: { dog: 'pugHangingPaws', cat: 'blackCat' },
} as const satisfies Record<
  keyof ServiceLandingQuickFactBands,
  Record<PetTypeEnum, QuickFactStickerName>
>;

export type ServiceLandingQuickFact = {
  sticker: (typeof QUICK_FACT_STICKERS)[QuickFactStickerName];
  text: React.ReactNode;
};

export type ServiceLandingQuickFacts = Record<
  keyof ServiceLandingQuickFactBands,
  ServiceLandingQuickFact
>;

// Millions read as "1.6 million" / "1,6 Millionen", rounded down because every fact says "Over"
// or "More than". Compact notation keeps two significant digits, so the value is floored to them
// first: `roundingMode: 'floor'` is not available before Chrome 106. Smaller counts keep grouping.
const formatQuickFactNumber = (value: number, locale: string): string => {
  if (value < 1_000_000) {
    return value.toLocaleString(locale);
  }

  const step = 10 ** (Math.floor(Math.log10(value)) - 1);
  return (Math.floor(value / step) * step).toLocaleString(locale, {
    notation: 'compact',
    compactDisplay: 'long',
  });
};

// A pet type without its own copy uses the default copy. A page with no pet type uses the dog
// copy when that is all there is.
const pickByPetType = (
  copyByPet: Partial<Record<ServiceLandingQuickFactPetKey, ServiceLandingQuickFactBands>>,
  petType: PetTypeEnum | undefined
): ServiceLandingQuickFactBands | undefined =>
  petType ? copyByPet[petType] ?? copyByPet.default : copyByPet.default ?? copyByPet.dog;

export const getServiceLandingQuickFacts = (
  internalServiceTypeSlug: string,
  petType: LocalizedBrandServicePetType | undefined,
  locale: string
): ServiceLandingQuickFacts | undefined => {
  const pagePetType =
    petType === PetTypeEnum.dog || petType === PetTypeEnum.cat ? petType : undefined;
  const bands = pickByPetType(
    DEFAULT_SERVICE_LANDING_QUICK_FACTS[internalServiceTypeSlug as ServiceTypeSlug] ?? {},
    pagePetType
  );

  if (!bands) {
    return undefined;
  }

  const format: ServiceLandingFormatNumber = (value) => formatQuickFactNumber(value, locale);
  const resolve = (
    text: ServiceLandingQuickFactText,
    stickers: Record<PetTypeEnum, QuickFactStickerName>
  ): ServiceLandingQuickFact => ({
    sticker: QUICK_FACT_STICKERS[stickers[pagePetType ?? PetTypeEnum.dog]],
    text: text(format),
  });

  return {
    first: resolve(bands.first, STICKERS_BY_BAND.first),
    second: resolve(bands.second, STICKERS_BY_BAND.second),
  };
};
